/**
 * Datos SNIES de la institución y sus directivos
 * (`/api/institucion-snies`).
 *
 * Alimenta el organigrama institucional.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

/**
 * La universidad ante el Ministerio, y los directivos de cada dependencia.
 *
 * La caracterización de la IES es lo mismo que el SNIES publica de cada
 * programa, un nivel más arriba. Los directivos cuelgan de un ÁREA —el rector
 * de la Rectoría, un decano de su facultad— y los que ya salieron se conservan
 * cerrados con `fecha_hasta`.
 */

export interface InstitucionSnies {
  id_institucion: number
  nombre: string
  id_area_raiz: number | null
  codigo_snies: string | null
  codigo_ies_padre: string | null
  nit: string | null
  principal_o_seccional: string | null
  naturaleza_juridica: string | null
  sector: string | null
  caracter_academico: string | null
  estado: string | null
  departamento: string | null
  municipio: string | null
  direccion: string | null
  telefono: string | null
  norma_creacion: string | null
  fecha_norma: string | null
  url: string | null
  acreditada_alta_calidad: boolean
  /** Derivados: mandan las resoluciones cuando las hay. */
  acreditacion_vigente?: boolean
  vence_acreditacion_calculado?: string | null
  bandera?: 'roja' | 'amarilla' | 'verde' | null
  resoluciones?: Array<{
    id_resolucion: number
    tipo_acto: string
    numero: string | null
    fecha_ejecutoria: string | null
    vigencia_anios: number | null
    fecha_vencimiento: string | null
    meses_para_vencer: number | null
    bandera: 'roja' | 'amarilla' | 'verde' | null
  }>
  vence_acreditacion: string | null
  resolucion_acreditacion: string | null
  area_raiz?: { id_programa: number; nombre: string; tipo?: string } | null
  directivos?: Directivo[]
}

/** Forma con la que el API de institucion snies devuelve este dato. */
export interface Directivo {
  id_directivo: number
  id_area: number
  nombre: string
  apellido: string | null
  cargo: string
  acto_nombramiento: string | null
  fecha_norma: string | null
  fecha_desde: string | null
  fecha_hasta: string | null
  email: string | null
  /** Derivados del backend. */
  nombre_completo: string
  vigente: boolean
}

/**
 * Consulta institucion snies · `GET /api/institucion`.
 *
 * @returns InstitucionSnies | null
 */
export async function getInstitucionService(): Promise<InstitucionSnies | null> {
  const r = await http.get<InstitucionSnies>('/api/institucion')
  return r.ok ? (r.data ?? null) : null
}

/**
 * Actualiza institucion snies · `PUT /api/institucion`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function actualizarInstitucionService(
  payload: Partial<InstitucionSnies>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.put('/api/institucion', payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo guardar la institución') }
}

/** Incluye los que ya salieron: se distinguen por `vigente`. */
export async function getDirectivosService(idArea: number): Promise<Directivo[]> {
  const r = await http.get<Directivo[]>(`/api/areas/${idArea}/directivos`)
  return r.ok ? (r.data ?? []) : []
}

/**
 * Crea institucion snies · `POST /api/areas`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function agregarDirectivoService(
  idArea: number,
  payload: Partial<Directivo>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.post(`/api/areas/${idArea}/directivos`, payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo registrar el directivo') }
}

/**
 * Actualiza institucion snies · `PUT /api/directivos`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function actualizarDirectivoService(
  id: number,
  payload: Partial<Directivo>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.put(`/api/directivos/${id}`, payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo actualizar el directivo') }
}

/**
 * Elimina institucion snies · `DELETE /api/directivos`.
 *
 * @returns boolean
 */
export async function eliminarDirectivoService(id: number): Promise<boolean> {
  const r = await http.delete(`/api/directivos/${id}`)
  return r.ok
}

/**
 * Actos administrativos de la IES.
 *
 * Comparten modelo y cálculo con los de un programa —vencimiento = ejecutoria +
 * vigencia, y de ahí la bandera—, así que se reutiliza `ResolucionPrograma` y su
 * catálogo de tipos. Para editar o borrar valen los mismos endpoints
 * `/api/resoluciones-programa/{id}`.
 */
export async function agregarResolucionInstitucionService(
  payload: Record<string, unknown>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.post('/api/institucion/resoluciones', payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo agregar la resolución') }
}
